import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
  type UIEvent,
} from "react"
import { useI18n } from "../hooks/useI18n"
import {
  getPlannedLanguagesForPicker,
  PLANNED_LANGUAGES,
  type PlannedLanguage,
  type PlannedLanguageCode,
} from "../i18n/languages"
import { FigmaSignalGlyph } from "./FigmaSignalGlyphLibrary"
import "./LanguageSelectionSheet.css"

const LANGUAGE_FLAG_URLS = import.meta.glob<string>(
  "../assets/circle-flags/language/generated/*.svg",
  { eager: true, query: "?url", import: "default" },
)

function languageFlagUrl(language: PlannedLanguage): string {
  return LANGUAGE_FLAG_URLS[`../assets/circle-flags/language/generated/${language.flagCode}.svg`] || ""
}

interface LanguageSelectionSheetProps {
  title: string
  selectedLanguageCode: PlannedLanguageCode | null
  isSaving?: boolean
  onSelect: (languageCode: PlannedLanguageCode) => void
  onSuggestOwn: () => void
  onBack: () => void
  onNext: (language: PlannedLanguage) => void
}

/**
 * Visual picker for the approved language set. Only languages marked active are
 * selectable. Runtime locale activation remains a separate concern.
 */
export function LanguageSelectionSheet({
  title,
  selectedLanguageCode,
  isSaving = false,
  onSelect,
  onSuggestOwn,
  onBack,
  onNext,
}: LanguageSelectionSheetProps) {
  const { locale, direction, t } = useI18n()
  const viewportRef = useRef<HTMLDivElement>(null)
  const dragStartYRef = useRef<number | null>(null)
  const dragOffsetRef = useRef(0)
  const [isScrolled, setIsScrolled] = useState(false)
  const [hasMoreBelow, setHasMoreBelow] = useState(true)
  const [dragOffset, setDragOffset] = useState(0)
  const [isDragging, setIsDragging] = useState(false)

  const languages = useMemo(
    () => getPlannedLanguagesForPicker((language) => language.selfLabel, locale),
    [locale],
  )

  const selectedLanguage = useMemo(
    () => PLANNED_LANGUAGES.find((language) => language.code === selectedLanguageCode) || null,
    [selectedLanguageCode],
  )

  const canContinue = selectedLanguage?.availability === "active"

  const updateScrollState = useCallback((element: HTMLDivElement) => {
    setIsScrolled(element.scrollTop > 0)
    setHasMoreBelow(element.scrollTop + element.clientHeight < element.scrollHeight - 1)
  }, [])

  useEffect(() => {
    const viewport = viewportRef.current
    if (!viewport) return
    updateScrollState(viewport)
  }, [languages, updateScrollState])

  const handleScroll = (event: UIEvent<HTMLDivElement>) => {
    updateScrollState(event.currentTarget)
  }

  const setSheetDragOffset = (value: number) => {
    const next = Math.max(0, value)
    dragOffsetRef.current = next
    setDragOffset(next)
  }

  const handleGrabberPointerDown = (event: ReactPointerEvent<HTMLSpanElement>) => {
    if (event.pointerType === "mouse" && event.button !== 0) return
    dragStartYRef.current = event.clientY
    setSheetDragOffset(0)
    setIsDragging(true)
    event.currentTarget.setPointerCapture(event.pointerId)
  }

  const handleGrabberPointerMove = (event: ReactPointerEvent<HTMLSpanElement>) => {
    if (dragStartYRef.current === null) return
    event.preventDefault()
    setSheetDragOffset(event.clientY - dragStartYRef.current)
  }

  const finishGrabberDrag = (event: ReactPointerEvent<HTMLSpanElement>, allowClose: boolean) => {
    const shouldClose = allowClose && dragOffsetRef.current >= 72
    dragStartYRef.current = null
    setSheetDragOffset(0)
    setIsDragging(false)
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
    if (shouldClose) onBack()
  }

  return (
    <section
      className={`bbf-country-selection bbf-language-selection${isDragging ? " is-dragging" : ""}`}
      style={{ "--bb-country-drag-y": `${dragOffset}px` } as CSSProperties}
      role="dialog"
      aria-modal="true"
      aria-labelledby="bbf-language-selection-title"
    >
      <span
        className="bbf-country-selection-grabber"
        aria-hidden="true"
        onPointerDown={handleGrabberPointerDown}
        onPointerMove={handleGrabberPointerMove}
        onPointerUp={(event) => finishGrabberDrag(event, true)}
        onPointerCancel={(event) => finishGrabberDrag(event, false)}
      />

      <h2 id="bbf-language-selection-title" className="bbf-country-selection-title" dir={direction}>{title}</h2>

      <div ref={viewportRef} className="bbf-country-selection-viewport" onScroll={handleScroll}>
        <div className="bbf-country-selection-list">
          {languages.map((language) => {
            const active = language.availability === "active"
            const selected = selectedLanguageCode === language.code
            const flagUrl = languageFlagUrl(language)
            return (
              <button
                type="button"
                key={language.code}
                className={`bbf-country-choice${selected ? " is-selected" : ""}${active ? " is-available" : " is-unavailable"}`}
                disabled={!active}
                aria-pressed={selected}
                onClick={() => active && onSelect(language.code)}
              >
                <span className="bbf-country-choice-flag" aria-hidden="true">
                  {flagUrl && <img src={flagUrl} alt="" />}
                </span>
                <span className="bbf-country-choice-label" dir={language.direction}>{language.selfLabel}</span>
                {!active && language.comingSoonLabel && (
                  <span className="bbf-country-choice-coming-soon" dir={language.direction}>
                    {language.comingSoonLabel}
                  </span>
                )}
              </button>
            )
          })}
          <button
            type="button"
            className="bbf-country-choice suggest-own"
            onClick={onSuggestOwn}
          >
            <span className="bbf-country-choice-flag bbf-country-choice-plus" aria-hidden="true">
              <FigmaSignalGlyph category="attention" slug="predlozhit-svoy-variant" />
            </span>
            <span className="bbf-country-choice-label" dir="ltr">Suggest your language</span>
          </button>
        </div>
      </div>

      {isScrolled && <span className="bbf-country-selection-fade top" aria-hidden="true" />}
      {hasMoreBelow && <span className="bbf-country-selection-fade bottom" aria-hidden="true" />}

      <div className="bbf-country-selection-actions">
        <button type="button" dir={direction} onClick={onBack} disabled={isSaving}>{t("common.back")}</button>
        <button
          type="button"
          className="next"
          dir={direction}
          disabled={!canContinue || isSaving}
          onClick={() => selectedLanguage && canContinue && !isSaving && onNext(selectedLanguage)}
        >
          {t("common.next")}
        </button>
      </div>
    </section>
  )
}
